import React from "react";
import { LayoutGrid, ChevronDown, AppWindow, Gamepad2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { getAppFromPathname } from "@/utils/app-route.utils";

interface AppOption {
  id: string;
  name: string;
  description: string;
  color: string;
  href: string;
  current?: boolean;
}

const apps: AppOption[] = [
  {
    id: "access-point",
    name: "Access Point",
    description: "Earn partnership management",
    color: "#3B82F6", // Bright Blue
    href: "/accesspoint",
    current: true,
  },
  {
    id: "reward-point",
    name: "Reward Point",
    description: "White labeled loyalty redemption store",
    color: "#8B5CF6", // Vivid Purple
    href: "/rewardpoint",
    current: false,
  },
  {
    id: "xpand-point",
    name: "Xpand Point",
    description: "2-way exchange points between programs",
    color: "#1EAEDB", // Bright Blue
    href: "/xpandpoint",
    current: false,
  },
  {
    id: "perk-point",
    name: "Perk Point",
    description: "Loyalty Management System (LMS)",
    color: "#22C55E", // Green
    href: "/perkpoint",
    current: false,
  },
  {
    id: "game-point",
    name: "Game Point",
    description: "Gamification — spin wheels, scratch cards, quizzes, and more",
    color: "#EA580C", // Orange — reads clearly different from Perk Point green
    href: "/gamepoint",
    current: false,
  },
  {
    id: "gift-card",
    name: "Gift OS",
    description: "Digital gift card management system",
    color: "#F59E0B", // Amber/Orange
    href: "/giftcard",
    current: false,
  },
  {
    id: "offers",
    name: "Offer Point",
    description: "Programs, merchants, and promotional campaigns",
    color: "#E11D48",
    href: "/offers",
    current: false,
  },
];

function AppIconFileName(app: AppOption) {
  switch (app.id) {
    case "access-point":
      return "AccessPoint.svg";
    case "xpand-point":
      return "XpandPoint.svg";
    case "reward-point":
      return "RewardPoint.svg";
    case "perk-point":
      return "PerkPoint.svg";
    case "gift-card":
      return "GiftCard.svg";
    case "offers":
      return "Offers.svg";
    default:
      return "AccessPoint.svg";
  }
}

function AppSwitcherTileIcon({ app }: { app: AppOption | undefined }) {
  if (!app) {
    return (
      <img
        src="/app-icons/AccessPoint.svg"
        alt=""
        style={{ width: 30, height: 30 }}
      />
    );
  }
  if (app.id === "game-point") {
    return <Gamepad2 className="h-[22px] w-[22px] text-white" strokeWidth={2} />;
  }
  return (
    <img
      src={`/app-icons/${AppIconFileName(app)}`}
      alt={app.name}
      style={{ width: 30, height: 30 }}
    />
  );
}

export function AppSwitcher() {
  const { expanded } = useSidebar();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeApp = getAppFromPathname(pathname);
  const isGamePoint = activeApp === "gamepoint";
  const isPerkPoint = activeApp === "perkpoint";
  const isXpandPoint = activeApp === "xpandpoint";
  const isRewardPoint = activeApp === "rewardpoint";
  const isGiftCard = activeApp === "giftcard";
  const isOffers = activeApp === "offers";
  const isAccessPoint = activeApp === "accesspoint";

  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");

  // const accessList: string[] = ["ACCESS_POINT"];
  const accessList: string[] = Array.isArray(userInfo?.applicationAccess)
    ? userInfo.applicationAccess
    : [];

  const filteredApps = apps.filter((app) => {
    // Only show apps that exist in applicationAccess array
    if (app.id === "access-point" && accessList.includes("ACCESS_POINT"))
      return true;
    if (app.id === "xpand-point" && accessList.includes("XPAND_POINT"))
      return true;
    if (app.id === "reward-point" && accessList.includes("REWARD_POINT"))
      return true;
    if (app.id === "perk-point" && accessList.includes("PERK_POINT"))
      return true;
    if (
      app.id === "game-point" &&
      (accessList.includes("PERK_POINT") ||
        accessList.includes("GAME_POINT") ||
        accessList.includes("GAMIFICATION"))
    )
      return true;
    if (app.id === "gift-card" && accessList.includes("GIFT_CARD")) return true;
    if (
      app.id === "offers" &&
      (accessList.includes("OFFERS") || accessList.includes("OFFER_POINT"))
    )
      return true;
    return false;
  });

  const currentApp = isOffers
    ? apps.find((app) => app.id === "offers")
    : isGamePoint
    ? apps.find((app) => app.id === "game-point")
    : isPerkPoint
    ? apps.find((app) => app.id === "perk-point")
    : isXpandPoint
    ? apps.find((app) => app.id === "xpand-point")
    : isRewardPoint
    ? apps.find((app) => app.id === "reward-point")
    : isGiftCard
    ? apps.find((app) => app.id === "gift-card")
    : isAccessPoint
    ? apps.find((app) => app.id === "access-point")
    : undefined;

  // Handle app switching
  const handleAppSwitch = (app: AppOption, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (app.href.startsWith("#")) {
      return; // Ignore non-implemented apps
    }

    // Close popovers first
    setMobileOpen(false);
    setPopoverOpen(false);

    // Navigate after a small delay to ensure popover closes
    setTimeout(() => {
      navigate(app.href);
    }, 100);
  };

  return (
    <>
      {/* Mobile App Switcher - Full screen sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild className="lg:hidden">
          <div
            className={cn(
              "flex items-center p-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors",
              expanded ? "justify-between" : "justify-center"
            )}
          >
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-md flex items-center justify-center"
                style={{ backgroundColor: currentApp?.color || "#3B82F6" }}
              >
                <AppSwitcherTileIcon app={currentApp} />
              </div>
              {expanded && (
                <div className="flex-1 truncate">
                  <p className="text-sm font-medium text-gray-900">
                    {currentApp?.name || "Access Point"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">Switch App</p>
                </div>
              )}
            </div>
            {expanded && <ChevronDown className="h-4 w-4 text-gray-500" />}
          </div>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle>Switch Application</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-2">
            {filteredApps.map((app) => (
              <button
                key={app.id}
                onClick={(e) => handleAppSwitch(app, e)}
                className={cn(
                  "flex flex-col p-4 rounded-xl border transition-all duration-200 hover:shadow-md",
                  (isOffers && app.id === "offers") ||
                    (isGamePoint && app.id === "game-point") ||
                    (isPerkPoint && app.id === "perk-point") ||
                    (isXpandPoint && app.id === "xpand-point") ||
                    (isRewardPoint && app.id === "reward-point") ||
                    (isGiftCard && app.id === "gift-card") ||
                    (isAccessPoint && app.id === "access-point")
                    ? "ring-2 ring-primary ring-offset-2"
                    : ""
                )}
              >
                <div className="flex items-center mb-2">
                  <div
                    className="h-10 w-10 rounded-md flex items-center justify-center mr-3"
                    style={{ backgroundColor: app.color }}
                  >
                    <AppSwitcherTileIcon app={app} />
                  </div>
                  <div>
                    <h3 className="font-medium">{app.name}</h3>
                    <p className="text-xs text-gray-500">{app.description}</p>
                    {(isOffers && app.id === "offers") ||
                    (isGamePoint && app.id === "game-point") ||
                    (isPerkPoint && app.id === "perk-point") ||
                    (isXpandPoint && app.id === "xpand-point") ||
                    (isRewardPoint && app.id === "reward-point") ||
                    (isGiftCard && app.id === "gift-card") ||
                    (isAccessPoint && app.id === "access-point") ? (
                      <span className="text-xs text-primary">Current</span>
                    ) : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop App Switcher - Popover */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              "hidden lg:flex items-center p-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors",
              expanded ? "justify-between" : "justify-center"
            )}
          >
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-md flex items-center justify-center"
                style={{ backgroundColor: currentApp?.color || "#3B82F6" }}
              >
                <AppSwitcherTileIcon app={currentApp} />
              </div>
              {expanded && (
                <div className="flex-1 truncate">
                  <p className="text-sm font-medium text-gray-900">
                    {currentApp?.name || "Access Point"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">Switch App</p>
                </div>
              )}
            </div>
            {expanded && <ChevronDown className="h-4 w-4 text-gray-500" />}
          </div>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="px-4 py-3 border-b">
            <h4 className="font-medium">Switch Application</h4>
            <p className="text-xs text-gray-500">
              Currently in: {currentApp?.name || "Access Point"}
            </p>
          </div>
          <div className="p-2 max-h-96 overflow-y-auto">
            {filteredApps.map((app) => (
              <button
                key={app.id}
                type="button"
                className={cn(
                  "flex items-center p-2 rounded-lg hover:bg-gray-100 transition-colors w-full text-left",
                  (isOffers && app.id === "offers") ||
                    (isGamePoint && app.id === "game-point") ||
                    (isPerkPoint && app.id === "perk-point") ||
                    (isXpandPoint && app.id === "xpand-point") ||
                    (isRewardPoint && app.id === "reward-point") ||
                    (isGiftCard && app.id === "gift-card") ||
                    (isAccessPoint && app.id === "access-point")
                    ? "bg-gray-50"
                    : ""
                )}
                onClick={(e) => handleAppSwitch(app, e)}
              >
                <div
                  className="h-8 w-8 rounded-md flex items-center justify-center mr-3"
                  style={{ backgroundColor: app.color }}
                >
                  <AppSwitcherTileIcon app={app} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">{app.name}</h4>
                    {(isOffers && app.id === "offers") ||
                    (isGamePoint && app.id === "game-point") ||
                    (isPerkPoint && app.id === "perk-point") ||
                    (isXpandPoint && app.id === "xpand-point") ||
                    (isRewardPoint && app.id === "reward-point") ||
                    (isGiftCard && app.id === "gift-card") ||
                    (!isOffers &&
                      !isGamePoint &&
                      !isPerkPoint &&
                      !isXpandPoint &&
                      !isRewardPoint &&
                      !isGiftCard &&
                      app.id === "access-point") ? (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Current
                      </span>
                    ) : null}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-xs text-gray-500">{app.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
